
import SecureLS from 'secure-ls'
import { useAuthStore } from '~/stores/useAuthStore'
import { useUIStore } from '~/stores/useUIStore'
import { consumeRecentLocationCapture } from '@/utils/recentLocationCapture'

const ls = new SecureLS({ isCompression: false })

export default defineNuxtPlugin((nuxtApp) => {
  const pinia = nuxtApp.$pinia

  const authStore = useAuthStore(pinia)
  const uiStore = useUIStore(pinia)

  // Read persisted state from SecureLS (same format as vuex-persistedstate used)
  try {
    const raw = ls.get('auth')
    if (raw) {
      const data = typeof raw === 'string' ? JSON.parse(raw) : raw

      // Hydrate auth accounts
      if (data.auth?.accounts) {
        authStore.accounts = data.auth.accounts
        authStore.guids = Object.keys(data.auth.accounts)
      }

      // Hydrate UI flags
      if (data.hideBanner != null) uiStore.setHideBanner(data.hideBanner)
      if (data.hideChristmasBanner != null) uiStore.setHideHolidayBanner(data.hideChristmasBanner)
    }
  } catch (e) {
    // Invalid data — ignore
  }

  const active = authStore.activeAccount
  let returningFromRecentLocationCapture = false

  if (active) {
    try {
      returningFromRecentLocationCapture = consumeRecentLocationCapture(window.sessionStorage, active.guid)
    } catch {
      // sessionStorage can be unavailable in restricted browser contexts.
    }
  }

  const pinRequired = active?.info?.location?.webPINRequired
  const pinHash = active?.info?.employee?.pinHash
  let shouldSyncActive = !!active

  if (pinRequired && pinHash) {
    const timeoutSeconds = active?.info?.location?.webPINTimeout || 0
    const lastActiveAt = active?.lastActiveAt || 0
    const elapsedSeconds = (Date.now() - lastActiveAt) / 1000

    if (elapsedSeconds > timeoutSeconds) {
      authStore.setLock({ locked: true })
      shouldSyncActive = false
    }
  }

  if (shouldSyncActive) {
    authStore.setReady(false)

    nuxtApp.hook('app:mounted', () => {
      const auth = nuxtApp.$auth as any
      if (typeof auth?.syncActive === 'function') {
        const freshInfo = returningFromRecentLocationCapture ? active?.info : undefined

        void auth.syncActive(freshInfo ? { info: freshInfo } : undefined).then(() => {
          void auth.syncWorkspace?.({ change: true })

          // The capture return can render from the info fetched immediately
          // before navigation. Refresh it in the background so normal session,
          // plan and permission checks remain authoritative.
          if (freshInfo) {
            void auth.syncInfo?.()
          }
        })
      }
    })
  }

  // Persist changes back to SecureLS
  const persist = () => {
    try {
      const data = {
        auth: { accounts: authStore.accounts },
        hideBanner: uiStore.hideBanner,
        hideChristmasBanner: uiStore.hideHolidayBanner,
      }
      ls.set('auth', JSON.stringify(data))
    } catch (e) {
      // SecureLS write failure — ignore
    }
  }

  authStore.$subscribe(persist)
  uiStore.$subscribe(persist)
})
